// src/products/userInvites/api/userInvitesApi.js
// Purpose: Enhanced API service for User Invites module - handles all invite-related API calls

import { supabase } from '../../../config';

const API_BASE = '/api/user-invites';

class UserInvitesApi {
  /**
   * Create a new user invitation
   */
  async createInvite(inviteData) {
    try {
      const response = await fetch(`${API_BASE}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inviteData)
      });

      // Check if response is valid JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('API not available (HTML response), falling back to direct Supabase call');
        return await this._createInviteDirect(inviteData);
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to create invitation');
      }

      return result;
    } catch (error) {
      console.error('Create invite error:', error);
      
      // If error is about JSON parsing, definitely fall back to Supabase
      if (error.message && error.message.includes('Unexpected token')) {
        console.log('API returned HTML instead of JSON, falling back to direct Supabase call');
        return await this._createInviteDirect(inviteData);
      }
      
      // For other errors, also fall back to Supabase
      return await this._createInviteDirect(inviteData);
    }
  }

  /**
   * Get user's invite settings
   */
  async getUserSettings(userId) {
    try {
      // Try API first
      const response = await fetch(`${API_BASE}/settings?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // Check if response is valid JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('API not available (HTML response), falling back to direct Supabase call');
        return await this._getUserSettingsDirect(userId);
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get settings');
      }

      return result;
    } catch (error) {
      console.error('Get settings error:', error);
      
      // If error is about JSON parsing, definitely fall back to Supabase
      if (error.message && error.message.includes('Unexpected token')) {
        console.log('API returned HTML instead of JSON, falling back to direct Supabase call');
        return await this._getUserSettingsDirect(userId);
      }
      
      // For other errors, also fall back to Supabase
      return await this._getUserSettingsDirect(userId);
    }
  }

  /**
   * Get user settings directly from Supabase (fallback)
   */
  async _getUserSettingsDirect(userId) {
    try {
      let { data, error } = await supabase
        .from('userinvites_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // No settings found, create default
        const defaultSettings = {
          user_id: userId,
          default_expiry_days: 7,
          auto_reminders: true,
          reminder_days: 3,
          default_role: 'viewer',
          require_email_confirmation: true,
          default_product: 'dataroom'
        };

        const { data: newData, error: insertError } = await supabase
          .from('userinvites_settings')
          .insert(defaultSettings)
          .select()
          .single();

        if (insertError) throw insertError;
        data = newData;
      } else if (error) {
        throw error;
      }

      // Map database column names to frontend property names
      const mappedData = {
        defaultExpiryDays: data?.default_expiry_days || 7,
        autoReminders: data?.auto_reminders || true,
        reminderDays: data?.reminder_days || 3,
        defaultRole: data?.default_role || 'viewer',
        requireEmailConfirmation: data?.require_email_confirmation || true,
        defaultProduct: data?.default_product || 'dataroom'
      };

      return {
        status: 'success',
        data: mappedData
      };
    } catch (error) {
      console.error('Direct Supabase get settings error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to get settings'
      };
    }
  }

  /**
   * Save user's invite settings
   */
  async saveUserSettings(settingsData) {
    try {
      // Try API first
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsData)
      });

      // Check if response is valid JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('API not available (HTML response), falling back to direct Supabase call');
        return await this._saveUserSettingsDirect(settingsData);
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save settings');
      }

      return result;
    } catch (error) {
      console.error('Save settings error:', error);
      
      // If error is about JSON parsing, definitely fall back to Supabase
      if (error.message && error.message.includes('Unexpected token')) {
        console.log('API returned HTML instead of JSON, falling back to direct Supabase call');
        return await this._saveUserSettingsDirect(settingsData);
      }
      
      // For other errors, also fall back to Supabase
      return await this._saveUserSettingsDirect(settingsData);
    }
  }

  /**
   * Save user settings directly to Supabase (fallback)
   */
  async _saveUserSettingsDirect(settingsData) {
    try {
      // Map frontend property names to database column names
      const dbData = {
        user_id: settingsData.user_id,
        default_expiry_days: settingsData.defaultExpiryDays,
        auto_reminders: settingsData.autoReminders,
        reminder_days: settingsData.reminderDays,
        default_role: settingsData.defaultRole,
        require_email_confirmation: settingsData.requireEmailConfirmation,
        default_product: settingsData.defaultProduct,
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('userinvites_settings')
        .upsert(dbData)
        .select()
        .single();

      if (error) throw error;

      return {
        status: 'success',
        data: data,
        message: 'Settings saved successfully'
      };
    } catch (error) {
      console.error('Direct Supabase save settings error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to save settings'
      };
    }
  }

  /**
   * Create invite directly in Supabase (fallback)
   */
  async _createInviteDirect(inviteData) {
    try {
      // Generate a unique token for the invite
      const token = crypto.randomUUID();
      
      // Calculate expiry date (default 7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const dbData = {
        email: inviteData.email,
        inviteforproduct: inviteData.inviteforproduct,
        role: inviteData.role,
        invitedby: inviteData.invitedby,
        invite_token: token,
        invite_status: 'pending',
        expires_at: expiresAt.toISOString(),
        custom_message: inviteData.custom_message || null,
        projectid: inviteData.projectid || null,
        roundid: inviteData.roundid || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('userinvites_invites')
        .insert(dbData)
        .select()
        .single();

      if (error) throw error;

      // Generate magic link URL
      const magicLinkUrl = `${window.location.origin}/accept-invite?token=${token}&product=${inviteData.inviteforproduct}`;

      // Try to send email via a simple API call
      try {
        await this._sendEmailDirect(inviteData.email, magicLinkUrl, inviteData.custom_message);
      } catch (emailError) {
        console.log('Email sending failed, but invite was created:', emailError);
      }

      return {
        status: 'success',
        data: {
          ...data,
          magic_link_url: magicLinkUrl
        },
        message: 'Invitation created successfully (email may not have been sent in development)'
      };
    } catch (error) {
      console.error('Direct Supabase create invite error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to create invitation'
      };
    }
  }

  /**
   * Send email directly via a simple API endpoint
   */
  async _sendEmailDirect(email, magicLinkUrl, customMessage) {
    try {
      // Try to call a simple email endpoint
      const response = await fetch('/api/send-invite-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          magic_link_url: magicLinkUrl,
          custom_message: customMessage
        })
      });

      if (!response.ok) {
        throw new Error(`Email API returned ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.log('Direct email sending failed:', error);
      throw error;
    }
  }

  /**
   * Get user's email templates
   */
  async getUserTemplates(userId, product = null) {
    try {
      let url = `${API_BASE}/templates?user_id=${userId}`;
      if (product) {
        url += `&product=${product}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // Check if response is valid JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('API not available (HTML response), falling back to direct Supabase call');
        return await this._getUserTemplatesDirect(userId, product);
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get templates');
      }

      return result;
    } catch (error) {
      console.error('Get templates error:', error);
      
      // If error is about JSON parsing, definitely fall back to Supabase
      if (error.message && error.message.includes('Unexpected token')) {
        console.log('API returned HTML instead of JSON, falling back to direct Supabase call');
        return await this._getUserTemplatesDirect(userId, product);
      }
      
      // For other errors, also fall back to Supabase
      return await this._getUserTemplatesDirect(userId, product);
    }
  }

  /**
   * Save user's email template
   */
  async saveUserTemplate(templateData) {
    try {
      const response = await fetch(`${API_BASE}/template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData)
      });

      // Check if response is valid JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('API not available (HTML response), falling back to direct Supabase call');
        return await this._saveUserTemplateDirect(templateData);
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save template');
      }

      return result;
    } catch (error) {
      console.error('Save template error:', error);
      
      // If error is about JSON parsing, definitely fall back to Supabase
      if (error.message && error.message.includes('Unexpected token')) {
        console.log('API returned HTML instead of JSON, falling back to direct Supabase call');
        return await this._saveUserTemplateDirect(templateData);
      }
      
      // For other errors, also fall back to Supabase
      return await this._saveUserTemplateDirect(templateData);
    }
  }

  /**
   * Get user's invite statistics
   */
  async getUserStats(userId) {
    try {
      // Try API first
      const response = await fetch(`${API_BASE}/stats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // If API returns HTML (404 or error page), fall back to direct Supabase
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('API not available, falling back to direct Supabase call');
        return await this._getUserStatsDirect(userId);
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get statistics');
      }

      return result;
    } catch (error) {
      console.error('Get stats error:', error);
      // Fall back to direct Supabase call
      return await this._getUserStatsDirect(userId);
    }
  }

  /**
   * Get user statistics directly from Supabase (fallback)
   */
  async _getUserStatsDirect(userId) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .select('invite_status, inviteforproduct, sentat')
        .eq('invitedby', userId);

      if (error) throw error;

      const now = new Date();
      const weekInMs = 7 * 24 * 60 * 60 * 1000;
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const stats = {
        total: data.length,
        pending: 0,
        accepted: 0,
        expired: 0,
        thisMonth: 0,
        byProduct: {}
      };

      data.forEach(invite => {
        const sentDate = new Date(invite.sentat);
        
        // Count by status
        if (invite.invite_status === 'accepted') {
          stats.accepted++;
        } else if (invite.invite_status === 'pending') {
          const isExpired = (now - sentDate) > weekInMs;
          if (isExpired) {
            stats.expired++;
          } else {
            stats.pending++;
          }
        }

        // Count this month
        if (sentDate >= thisMonth) {
          stats.thisMonth++;
        }

        // Count by product
        if (!stats.byProduct[invite.inviteforproduct]) {
          stats.byProduct[invite.inviteforproduct] = 0;
        }
        stats.byProduct[invite.inviteforproduct]++;
      });

      return {
        status: 'success',
        data: stats
      };
    } catch (error) {
      console.error('Direct Supabase get stats error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to get statistics'
      };
    }
  }

  /**
   * Get user templates directly from Supabase (fallback)
   */
  async _getUserTemplatesDirect(userId, product = null) {
    try {
      let query = supabase
        .from('userinvites_templates')
        .select('*')
        .eq('user_id', userId);

      if (product && product !== 'all') {
        query = query.or(`product.eq.${product},product.eq.all`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return {
        status: 'success',
        data: data || []
      };
    } catch (error) {
      console.error('Direct Supabase get templates error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to get templates'
      };
    }
  }

  /**
   * Save user template directly to Supabase (fallback)
   */
  async _saveUserTemplateDirect(templateData) {
    try {
      const dbData = {
        user_id: templateData.user_id,
        name: templateData.name,
        product: templateData.product,
        subject: templateData.subject,
        html_content: templateData.html_content,
        is_default: templateData.is_default || false,
        updated_at: new Date().toISOString()
      };

      // If it's a new template (no ID or ID starts with 'template_'), insert
      if (!templateData.id || templateData.id.startsWith('template_')) {
        const { data, error } = await supabase
          .from('userinvites_templates')
          .insert(dbData)
          .select()
          .single();

        if (error) throw error;
        return {
          status: 'success',
          data: data,
          message: 'Template saved successfully'
        };
      } else {
        // Update existing template
        const { data, error } = await supabase
          .from('userinvites_templates')
          .update(dbData)
          .eq('id', templateData.id)
          .select()
          .single();

        if (error) throw error;
        return {
          status: 'success',
          data: data,
          message: 'Template updated successfully'
        };
      }
    } catch (error) {
      console.error('Direct Supabase save template error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to save template'
      };
    }
  }

  /**
   * Resend an invitation
   */
  async resendInvite(inviteId) {
    try {
      const response = await fetch(`${API_BASE}/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invite_id: inviteId })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to resend invitation');
      }

      return result;
    } catch (error) {
      console.error('Resend invite error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to resend invitation'
      };
    }
  }

  /**
   * Get all invites sent by a user (enhanced with filtering)
   */
  async getInvitesByUser(userId, filters = {}) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .select('*')
        .eq('invitedby', userId)
        .order('sentat', { ascending: false });

      if (error) throw error;

      return {
        status: 'success',
        data: data || []
      };
    } catch (error) {
      console.error('Get invites error:', error);
      return {
        status: 'error',
        message: error.message,
        data: []
      };
    }
  }

  /**
   * Get invites for a specific CapRaise round
   */
  async getInvitesByRound(roundId) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .select('*')
        .eq('roundid', roundId)
        .eq('inviteforproduct', 'capraise')
        .order('sentat', { ascending: false });

      if (error) throw error;

      return {
        status: 'success',
        data: data || []
      };
    } catch (error) {
      console.error('Get invites by round error:', error);
      return {
        status: 'error',
        message: error.message,
        data: []
      };
    }
  }

  /**
   * Get invite by token
   */
  async getInviteByToken(token) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .select('*')
        .eq('invite_token', token)
        .single();

      if (error) throw error;

      return {
        status: 'success',
        data
      };
    } catch (error) {
      console.error('Get invite by token error:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }

  /**
   * Accept an invitation
   */
  async acceptInvite(inviteId, userId) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .update({
          invite_status: 'accepted',
          acceptedby: userId,
          acceptedat: new Date().toISOString()
        })
        .eq('id', inviteId)
        .select()
        .single();

      if (error) throw error;

      return {
        status: 'success',
        data
      };
    } catch (error) {
      console.error('Accept invite error:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Revoke an invitation
   */
  async revokeInvite(inviteId) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .update({
          invite_status: 'revoked'
        })
        .eq('id', inviteId)
        .select()
        .single();

      if (error) throw error;

      return {
        status: 'success',
        data
      };
    } catch (error) {
      console.error('Revoke invite error:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Grant product access to user
   */
  async grantProductAccess(userId, productName, role) {
    try {
      const { data, error } = await supabase
        .from('user_product_roles')
        .upsert({
          user_id: userId,
          product_name: productName,
          role: role
        })
        .select()
        .single();

      if (error) throw error;

      return {
        status: 'success',
        data
      };
    } catch (error) {
      console.error('Grant product access error:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Grant dataroom project access
   */
  async grantDataroomAccess(userId, projectId, role = 'viewer') {
    try {
      const { data, error } = await supabase
        .from('dataroom_users')
        .upsert({
          user_id: userId,
          project_id: projectId,
          role: role,
          status: 'active',
          invited_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      return {
        status: 'success',
        data
      };
    } catch (error) {
      console.error('Grant dataroom access error:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Check if email is already invited to product/context
   */
  async checkExistingInvite(email, product, projectId = null, roundId = null) {
    try {
      let query = supabase
        .from('userinvites_invites')
        .select('*')
        .eq('email', email)
        .eq('inviteforproduct', product)
        .in('invite_status', ['pending', 'accepted']);

      if (projectId) {
        query = query.eq('projectid', projectId);
      }
      if (roundId) {
        query = query.eq('roundid', roundId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return {
        status: 'success',
        exists: data && data.length > 0,
        data: data || []
      };
    } catch (error) {
      console.error('Check existing invite error:', error);
      return {
        status: 'error',
        message: error.message,
        exists: false
      };
    }
  }

  /**
   * Get invitation statistics for a user
   */
  async getInviteStats(userId) {
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .select('invite_status, inviteforproduct, sentat')
        .eq('invitedby', userId);

      if (error) throw error;

      const stats = {
        total: data.length,
        pending: 0,
        accepted: 0,
        expired: 0,
        byProduct: {}
      };

      const now = new Date();
      const weekInMs = 7 * 24 * 60 * 60 * 1000; // 7 days

      data.forEach(invite => {
        // Count by status
        if (invite.invite_status === 'accepted') {
          stats.accepted++;
        } else if (invite.invite_status === 'pending') {
          const sentDate = new Date(invite.sentat);
          const isExpired = (now - sentDate) > weekInMs;
          if (isExpired) {
            stats.expired++;
          } else {
            stats.pending++;
          }
        }

        // Count by product
        if (!stats.byProduct[invite.inviteforproduct]) {
          stats.byProduct[invite.inviteforproduct] = 0;
        }
        stats.byProduct[invite.inviteforproduct]++;
      });

      return {
        status: 'success',
        data: stats
      };
    } catch (error) {
      console.error('Get invite stats error:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }


  /**
   * Process invite acceptance and grant appropriate access
   */
  async processInviteAcceptance(invite, userId) {
    try {
      // Mark invite as accepted
      await this.acceptInvite(invite.id, userId);

      // Grant product access
      if (invite.role) {
        await this.grantProductAccess(userId, invite.inviteforproduct, invite.role);
      }

      // Grant specific context access based on product
      if (invite.inviteforproduct === 'dataroom' && invite.projectid) {
        await this.grantDataroomAccess(userId, invite.projectid, invite.role || 'viewer');
      }

      // For CapRaise, access is typically managed through the capraise_investors table
      // This would be handled by the CapRaise module when the user accesses the round

      return {
        status: 'success',
        message: 'Invite accepted and access granted'
      };
    } catch (error) {
      console.error('Process invite acceptance error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to process invite acceptance'
      };
    }
  }

  /**
   * Bulk invite multiple users
   */
  async bulkInvite(inviteList) {
    try {
      const results = [];
      
      for (const inviteData of inviteList) {
        const result = await this.createInvite(inviteData);
        results.push({
          email: inviteData.email,
          ...result
        });
      }

      const successful = results.filter(r => r.status === 'success').length;
      const failed = results.filter(r => r.status === 'error').length;

      return {
        status: 'success',
        data: {
          results,
          summary: {
            total: inviteList.length,
            successful,
            failed
          }
        }
      };
    } catch (error) {
      console.error('Bulk invite error:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to send bulk invitations'
      };
    }
  }
}

export const userInvitesApi = new UserInvitesApi();